import React, { useState, useRef, useEffect } from "react";
import { SvgIcon } from "@montana/shared/svg-icon";
import { FieldWrapper } from "@montana/shared/form/field-wrapper";
import { withForm } from "@montana/shared/form/field";
import { isFieldTouched, isFieldValid } from "@montana/shared/form/field/field.component";
import { ISharedFieldProperties } from "@montana/shared/form/field/field";
import { useTranslations } from "@montana/containers/translations";

type IAmountBaseProps = Pick<React.InputHTMLAttributes<HTMLInputElement>, | 'step' | 'id' | 'value'>
    & ISharedFieldProperties;

export interface IAmountProps extends IAmountBaseProps {
    noLimits?: boolean; // Set this to true to remove min- and max-limits
    defaultValue?: string | number;
    min?: number;
    max?: number;
    hiddenLabel?: boolean;
    bottomMargin?: boolean;
}

let amountFieldCounter = 0;

export const Amount: React.FC<IAmountProps> = React.memo((props: IAmountProps): JSX.Element => {
    const {
        noLimits,
        step = 1,
        defaultValue = "",
        id,
        label,
        onChange,
        onBlur,
        value: valueFromProps,
        min = noLimits ? -Infinity : 0,
        max = Infinity,
        hiddenLabel,
        autoFocus,
        bottomMargin = true,
    } = props;

    const value = Math.min(Math.max(Number(valueFromProps !== undefined ? valueFromProps : defaultValue), min), max);

    // We keep a seperate for displayed value, that is not directly commited to the given value, but
    // delayed until onBlur (ie. while amount input field is in focus we let the user type whatever,
    // then clean that up and align stuff onBlur)
    const [displayValue, setDisplayValue] = useState(value.toString());

    const [usedID] = useState(id || `amount-${amountFieldCounter += 1}`);

    const changeValue = (newValue: number): void => {
        const returnValue = Math.min(Number(max), Math.max(Number(min), (Number.isNaN(newValue) ? 1 : newValue) || 0));
        const deviation = (returnValue - Number(min)) % Number(step);

        const calculatedValue = !deviation
            // If the value (minus the minimum "min") is divisible by "step", all is good in the hood.
            ? returnValue
            // ... otherwise we'll set the value to something close, that lives up to this criteria.
            : returnValue - deviation;


        if (typeof onChange === "function") {
            onChange(calculatedValue.toString());
        }
        setDisplayValue(calculatedValue.toString());
    };

    const translate = useTranslations();
    const translatedTexts = {
        labelInputDefault: translate((translations): string => translations.forms.selectQuantity, "Mængde"),
        labelIncreaseButton: translate((translations): string => translations.forms.increase, "Øg"),
        labelDecreaseButton: translate((translations): string => translations.forms.decrease, "Mindsk"),
    };

    const inputElement = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (autoFocus && inputElement.current) {
            inputElement.current.focus();
        }
    }, []);

    return (
        <FieldWrapper
            label={label as string || translatedTexts.labelInputDefault}
            id={usedID}
            className="amount"
            flex
            hiddenLabel={hiddenLabel}
            inlineLabel={false}
            bottomMargin={bottomMargin}
        >
            <button
                type="button"
                className="amount__button"
                onClick={(): void => changeValue(value - Number(step))}
                aria-controls={usedID}
                aria-label={translatedTexts.labelDecreaseButton}
                disabled={value === min}
            >
                <SvgIcon iconName="minus" />
            </button>

            <input
                ref={inputElement}
                size={10}
                type="tel"
                pattern="[0-9]*"
                inputMode="numeric"
                value={displayValue}
                id={usedID}
                aria-labelledby={`${usedID}-label`}
                className="amount__inputfield"
                onChange={(event): void => setDisplayValue(event.currentTarget.value)}
                onBlur={(event): void => {
                    const newValue = Number(event.currentTarget.value);
                    // If new value is not a valid number, just fall back to whatever the
                    // value was before the field was in focus
                    changeValue(!Number.isNaN(newValue) ? newValue : value);
                    if (typeof onBlur === "function") {
                        onBlur(event);
                    }
                }}
            />

            <SvgIcon className="amount__icon" iconName="up_down" />

            <button
                type="button"
                className="amount__button"
                onClick={(): void => changeValue(value + Number(step))}
                aria-controls={usedID}
                aria-label={translatedTexts.labelIncreaseButton}
                disabled={value === max}
            >
                <SvgIcon iconName="plus" />
            </button>
        </FieldWrapper>
    );
});

export const FormAmount = withForm<
    IAmountProps,
    'value' | 'onChange' | 'isValid' | "isTouched" | "errorMessage"
>(Amount, (field, form): IAmountProps => ({
    onChange: (newValue: string): void => form.setFieldValue(field.name, newValue),
    onBlur: form.handleBlur,
    value: field.value,
    errorMessage: (form.errors as { [key: string]: string })[field.name],
    isValid: isFieldValid(field.name, form),
    isTouched: isFieldTouched(field.name, form),
}));
